import { FormattedMessage, useIntl } from 'react-intl';
import { FormTextField, FormControl, FormSelect, LoadingButton } from 'components/common';
import { FcaTextfield } from 'pages/SignupPage/NewAuthFormComponents/FcaTextfield';
import { useFormContext, useWatch } from 'react-hook-form';
import { AuthorisationType } from 'pages/SignupPage/dtos/firm.dto';
import { isValidGbPhoneNumber } from 'utils/gbPhoneNumber';
import { SignupOrganisationFormFields } from '../signupOrganisationForm.types';
import { AddressFields } from '../AddressFields';
import { addressFieldPathsFor, isAddressManualPathFor, orgAddressValuePath } from '../addressFieldPaths';
import { brokerOrgFormRoot, firmField, formPath, orgField } from '../formFieldPaths';
import { rulesRequiredYesNo, yesNoBooleanRadioOptions } from '../yesNoRadioOptions';

const textFieldIn = {
  type: 'text' as const,
  inputProps: { sx: { padding: '6px !important' } as const, className: 'text-sm !font-normal' as const },
};

type FirmFormProps = {
  isFcaLoading: boolean;
  onFcaFind: () => void;
  showFieldsAfterFca: boolean;
};

export function FirmForm({ isFcaLoading, onFcaFind, showFieldsAfterFca }: FirmFormProps) {
  const intl = useIntl();
  const form = useFormContext<SignupOrganisationFormFields>();
  const base = brokerOrgFormRoot.firm;
  const backendId = useWatch({ name: formPath(base, orgField.backendId), control: form.control });
  const authorisationType = useWatch({ name: formPath(base, firmField.authorisationType), control: form.control });
  const locked = Boolean(backendId);
  const required = intl.formatMessage({ id: 'common.error.requiredField' });
  const showRest = showFieldsAfterFca || locked;

  return (
    <div className='grid w-full max-w-4xl grid-cols-1 gap-4 md:grid-cols-2'>
      {locked && (
        <div className='md:col-span-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900'>
          <FormattedMessage id='auth.signup.existingFirmInfo' />
        </div>
      )}

      <div className='flex flex-col gap-2 sm:flex-row sm:items-end md:col-span-2'>
        <div className='min-w-0 grow'>
          <FcaTextfield name={formPath(base, orgField.financialConductAuthorityNumber)} locked={locked} />
        </div>
        {!isFcaLoading && !showFieldsAfterFca && (
          <LoadingButton
            isLoading={isFcaLoading}
            buttonProps={{
              type: 'button',
              variant: 'contained',
              className: 'h-9 shrink-0',
              onClick: onFcaFind,
              disabled: locked,
            }}
          >
            <FormattedMessage id='cta.continue' />
          </LoadingButton>
        )}
      </div>

      {showRest && (
        <>
          <div>
            <FormattedMessage id='auth.signup.companyName' />
            <FormTextField
              name={formPath(base, orgField.name)}
              rules={locked ? undefined : { required: { value: true, message: required } }}
              disabled={locked}
              textFieldProps={{
                placeholder: intl.formatMessage({ id: 'placeholders.companyDetails.name' }),
                ...textFieldIn,
              }}
            />
          </div>
          <div>
            <FormattedMessage id='auth.signup.companyNumber' />
            <FormTextField
              name={formPath(base, firmField.companyNumber)}
              rules={
                locked
                  ? undefined
                  : {
                      minLength: { value: 8, message: intl.formatMessage({ id: 'common.error.validCompanyNumber' }) },
                      maxLength: { value: 8, message: intl.formatMessage({ id: 'common.error.validCompanyNumber' }) },
                      required: { value: true, message: required },
                      pattern: {
                        value: /^(?:[A-Z]{2}\d{6}|[A-Z]\d{7}|\d{8})$/,
                        message: intl.formatMessage({ id: 'common.error.validCompanyNumber' }),
                      },
                    }
              }
              disabled={locked}
              textFieldProps={{
                placeholder: intl.formatMessage({ id: 'placeholders.companyDetails.number' }),
                ...textFieldIn,
              }}
            />
          </div>
          <div>
            <FormattedMessage id='auth.signup.website' />
            <FormTextField
              name={formPath(base, firmField.website)}
              rules={
                locked
                  ? undefined
                  : {
                      pattern: {
                        value:
                          /^(https?:\/\/)?(www\.)?[a-zA-Z0-9\-@:%._+~#=]{2,256}\.[a-zA-Z]{2,6}\b([-a-zA-Z0-9@:%_+.~#?&/=]*)$/i,
                        message: intl.formatMessage({ id: 'common.error.validUrl' }),
                      },
                    }
              }
              disabled={locked}
              textFieldProps={{
                placeholder: intl.formatMessage({ id: 'placeholders.companyDetails.website' }),
                ...textFieldIn,
              }}
            />
          </div>
          <div>
            <FormattedMessage id='auth.signup.companyPhoneNumber' />
            <FormTextField
              name={formPath(base, firmField.phoneNumber)}
              rules={
                locked
                  ? undefined
                  : {
                      required: { value: true, message: required },
                      validate: value => {
                        const trimmed = String(value ?? '').trim();

                        if (!trimmed) {
                          return true;
                        }

                        return (
                          isValidGbPhoneNumber(trimmed) || intl.formatMessage({ id: 'common.error.validPhoneNumber' })
                        );
                      },
                    }
              }
              disabled={locked}
              textFieldProps={{
                placeholder: intl.formatMessage({ id: 'placeholders.companyDetails.phoneNumber' }),
                ...textFieldIn,
              }}
            />
          </div>
          <div>
            <FormattedMessage id='auth.signup.principalOrDirectorName' />
            <FormTextField
              name={formPath(base, firmField.principalOrDirectorName)}
              disabled={locked}
              textFieldProps={{
                placeholder: intl.formatMessage({ id: 'placeholders.companyDetails.principalOrDirectorName' }),
                ...textFieldIn,
              }}
            />
          </div>
          <div>
            <FormattedMessage id='auth.signup.informationCommissionersOfficeNumber' />
            <FormTextField
              name={formPath(base, firmField.informationCommissionersOfficeNumber)}
              disabled={locked}
              textFieldProps={textFieldIn}
            />
          </div>
          <div className='md:col-span-2'>
            <div className='mb-1 text-sm font-medium'>Company address</div>
            <AddressFields
              fieldPaths={addressFieldPathsFor(base)}
              manualTogglePath={isAddressManualPathFor(base)}
              addressValuePath={orgAddressValuePath(base)}
              disabled={locked}
            />
          </div>
          <div>
            <FormattedMessage id='auth.signup.authorisationType' />
            <FormSelect
              name={formPath(base, firmField.authorisationType)}
              rules={locked ? undefined : { required: { value: true, message: required } }}
              options={[
                {
                  label: intl.formatMessage({ id: 'authorisationType.directlyAuthorised' }),
                  value: AuthorisationType.DIRECTLY_AUTHORISED,
                },
                {
                  label: intl.formatMessage({ id: 'authorisationType.authorisedRepresentative' }),
                  value: AuthorisationType.AUTHORISED_REPRESENTATIVE,
                },
              ]}
              isDisabled={locked}
              selectInputProps={{
                InputProps: { sx: { padding: '0 !important' } },
                placeholder: intl.formatMessage({ id: 'placeholders.companyDetails.authorisationType' }),
              }}
            />
          </div>
          {authorisationType === AuthorisationType.DIRECTLY_AUTHORISED && !locked && (
            <>
              <div>
                <FormattedMessage id='auth.signup.companyHppPermissions' />
                <FormControl
                  name={formPath(base, firmField.hasHppPermissions)}
                  rules={rulesRequiredYesNo(required)}
                  columnsAmount={2}
                  noPadding
                  radioOptions={yesNoBooleanRadioOptions}
                />
              </div>
              <div>
                <FormattedMessage id='auth.signup.isPartOfMortgageClub' />
                <FormControl
                  name={formPath(base, firmField.isPartOfClub)}
                  rules={rulesRequiredYesNo(required)}
                  columnsAmount={2}
                  noPadding
                  radioOptions={yesNoBooleanRadioOptions}
                />
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
